// A venue's tax registration — its tax mode and GSTIN — as a **dated setting** (F-PAY-11; spec/06
// §10.3–§10.4, spec/03 §9, spec/04 §6).
//
//   GET  /v1/organizations/{o}/venues/{v}/tax-registration                       read it            tax:manage
//   POST /v1/organizations/{o}/venues/{v}/tax-registration                       schedule a change  tax:manage
//   POST /v1/organizations/{o}/venues/{v}/tax-registration/{id}/withdraw         withdraw a change  tax:manage
//
// `tax.mode` and `tax.gstin` are dated settings (spec/03 §9): their values are not rows in
// `setting_values` but rows in `venue_tax_registration`, and they change **only** through this
// endpoint — never through `PUT …/settings`, which refuses them (spec/06 §10.4 rule 2). A change:
//
//   1. **Is dated** — the new mode, the GSTIN that mode needs, and the business day it takes effect
//      from: today or a later day in the venue's timezone, never an earlier one, because documents
//      already issued are never reissued (spec/06 §10.4 rule 1).
//   2. **Is audited** — one row, written in one transaction with its audit row (before, after and the
//      effective day).
//   3. **Can be withdrawn until it starts** — a change dated later is `scheduled` and can be withdrawn
//      until its business day begins; once its day has begun it is permanent (spec/06 §10.4 rule 3).
//
// The value **in force** on a business day is the latest not-withdrawn change whose day has begun; with
// none, the registry default (`unregistered`, no GSTIN) applies (spec/04 §6), so a new venue sells with
// plain bills from its first sale (spec/06 §10.3 rule 2).
//
// **GSTIN.** 15 characters in the GSTIN format with a valid check character (spec/06 §10.3 rule 1),
// required for `regular` and `composition` and absent for `unregistered`. That the first two digits are
// the state code of the venue's address is checked server-side, where the address is known.
import { createRoute } from '@hono/zod-openapi';
import { z } from "../zod.js";
import { VenueIdSchema, VenueTaxRegistrationIdSchema } from "../ids/schema.js";
import { IsoDateTimeSchema, LocalDateSchema } from "../primitives/scalars.js";
import { VenueScopeParamsSchema } from "../primitives/scope.js";
import { commonProblemResponses, JSON_CONTENT_TYPE, problemResponse } from "../openapi/responses.js";
import { TAX_MANAGE, VENUES_READ } from "../permissions/index.js";
import { PERMISSION_EXTENSION } from "./audit.js";
import { SaleTaxModeSchema } from "./commerce.js";
import { GstinSchema } from "../primitives/gstin.js";
// The GSTIN primitives live in `primitives/gstin.ts` (a sale's B2B buyer uses them too, F-CMP-07); re-exported here.
export { GSTIN_PATTERN, GstinSchema, gstinCheckCharacter, isValidGstin } from "../primitives/gstin.js";
// ---------------------------------------------------------------------------------------------
// The dated change, and the view
// ---------------------------------------------------------------------------------------------
/** Where the venue's current tax treatment comes from: a recorded registration, or the registry default. */
export const TaxRegistrationSourceSchema = z.enum(['default', 'registration']).openapi('TaxRegistrationSource', {
    description: 'Whether the current tax mode comes from a recorded registration or the registry default (unregistered).',
});
export const TaxRegistrationStatusSchema = z
    .enum(['in_force', 'scheduled', 'withdrawn', 'superseded'])
    .openapi('TaxRegistrationStatus', {
    description: 'A dated change: in_force (the one applying now), scheduled (dated later, withdrawable), withdrawn, or superseded by a later change whose day has also begun.',
});
export const TaxRegistrationSchema = z
    .object({
    id: VenueTaxRegistrationIdSchema,
    mode: SaleTaxModeSchema,
    /** The GSTIN this mode was recorded with; null for an `unregistered` change. */
    gstin: z.string().nullable(),
    /** The business day it takes effect from (the venue's timezone). */
    effectiveOn: LocalDateSchema,
    reason: z.string().nullable(),
    status: TaxRegistrationStatusSchema,
    recordedAt: IsoDateTimeSchema,
    /** When it was withdrawn; null unless withdrawn. */
    withdrawnAt: IsoDateTimeSchema.nullable(),
})
    .openapi('TaxRegistration', { description: 'One dated change of a venue’s tax mode and GSTIN (spec/06 §10.4).' });
export const ResolvedTaxRegistrationSchema = z
    .object({
    mode: SaleTaxModeSchema,
    gstin: z.string().nullable(),
    /** The effective day of the change in force, or null when the value is the registry default. */
    effectiveOn: LocalDateSchema.nullable(),
    source: TaxRegistrationSourceSchema,
})
    .openapi('ResolvedTaxRegistration', {
    description: 'The venue’s tax mode and GSTIN in force on the business day asked about (spec/06 §10.4).',
});
export const TaxRegistrationViewSchema = z
    .object({
    venueId: VenueIdSchema,
    /** The venue's current business day, which `current` is resolved for. */
    businessDay: LocalDateSchema,
    /** The mode and GSTIN in force now. */
    current: ResolvedTaxRegistrationSchema,
    /** Changes dated in the future that have not been withdrawn, soonest first — each can still be withdrawn. */
    scheduled: z.array(TaxRegistrationSchema),
    /** Every change ever recorded for the venue, newest first. */
    history: z.array(TaxRegistrationSchema),
})
    .openapi('TaxRegistrationView', {
    description: 'A venue’s tax registration: what is in force now, what is scheduled, and its history (spec/06 §10.4).',
});
export const ScheduleTaxRegistrationSchema = z
    .object({
    mode: SaleTaxModeSchema,
    /** The GSTIN the mode needs. Required for `regular` and `composition`, and refused for `unregistered`. */
    gstin: GstinSchema.optional(),
    /** The business day it takes effect from: today or later in the venue's timezone, never earlier. */
    effectiveOn: LocalDateSchema,
    reason: z.string().trim().min(1).max(500).optional(),
})
    .strict()
    .superRefine((change, context) => {
    const needsGstin = change.mode === 'regular' || change.mode === 'composition';
    if (needsGstin && change.gstin === undefined) {
        context.addIssue({ code: 'custom', message: `A ${change.mode} venue needs a GSTIN.`, path: ['gstin'] });
    }
    if (!needsGstin && change.gstin !== undefined) {
        context.addIssue({ code: 'custom', message: 'An unregistered venue has no GSTIN.', path: ['gstin'] });
    }
})
    .openapi('ScheduleTaxRegistration', {
    description: 'Record a dated change of a venue’s tax mode and GSTIN (spec/06 §10.4).',
});
const TaxRegistrationParamsSchema = VenueScopeParamsSchema.extend({
    registrationId: VenueTaxRegistrationIdSchema,
}).openapi('TaxRegistrationParams');
// ---------------------------------------------------------------------------------------------
// The routes
// ---------------------------------------------------------------------------------------------
const viewResponse = (description) => ({
    description,
    content: { [JSON_CONTENT_TYPE]: { schema: TaxRegistrationViewSchema } },
});
export const getVenueTaxRegistrationRoute = createRoute({
    method: 'get',
    path: '/v1/organizations/{organizationId}/venues/{venueId}/tax-registration',
    operationId: 'getVenueTaxRegistration',
    tags: ['tax'],
    summary: 'Read a venue’s tax registration',
    description: 'The venue’s tax mode and GSTIN in force now, the changes scheduled for later, and its history (spec/06 §10.4). Reading it needs only `venues:read` (spec/07 §6.3).',
    [PERMISSION_EXTENSION]: VENUES_READ,
    request: { params: VenueScopeParamsSchema },
    responses: {
        200: viewResponse('The venue’s tax registration.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: commonProblemResponses[403],
        404: problemResponse('No such venue in this organisation.', 'not-found'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const scheduleVenueTaxRegistrationRoute = createRoute({
    method: 'post',
    path: '/v1/organizations/{organizationId}/venues/{venueId}/tax-registration',
    operationId: 'scheduleVenueTaxRegistration',
    tags: ['tax'],
    summary: 'Record a dated change of a venue’s tax mode',
    description: 'Records a change to `regular`, `composition` or `unregistered`, with the GSTIN a registered mode needs, effective from a business day — today or later, never earlier (spec/06 §10.4). ' +
        'A `regular` or `composition` change needs the organisation’s legal name and registered address recorded first, because its documents are issued under them ' +
        '(`409 business-details-required`, ADR-0046); `unregistered` needs neither. Audited. Requires `tax:manage`.',
    [PERMISSION_EXTENSION]: TAX_MANAGE,
    request: {
        params: VenueScopeParamsSchema,
        body: { content: { [JSON_CONTENT_TYPE]: { schema: ScheduleTaxRegistrationSchema } }, required: true },
    },
    responses: {
        201: viewResponse('The venue’s tax registration, with the change recorded.'),
        400: problemResponse('The change is not one the registry accepts, or its GSTIN is invalid.', 'validation-failed'),
        401: commonProblemResponses[401],
        403: commonProblemResponses[403],
        404: problemResponse('No such venue in this organisation.', 'not-found'),
        409: problemResponse('The effective day is in the past, or the organisation’s legal name or registered address is not recorded yet.', 'conflict', 'business-details-required'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const withdrawVenueTaxRegistrationRoute = createRoute({
    method: 'post',
    path: '/v1/organizations/{organizationId}/venues/{venueId}/tax-registration/{registrationId}/withdraw',
    operationId: 'withdrawVenueTaxRegistration',
    tags: ['tax'],
    summary: 'Withdraw a scheduled tax change',
    description: 'Withdraws a change dated in the future before its business day begins (spec/06 §10.4 rule 3). Once its day has begun it is permanent. Audited. Requires `tax:manage`.',
    [PERMISSION_EXTENSION]: TAX_MANAGE,
    request: { params: TaxRegistrationParamsSchema },
    responses: {
        200: viewResponse('The venue’s tax registration, with the change withdrawn.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: commonProblemResponses[403],
        404: problemResponse('No such scheduled change for this venue.', 'not-found'),
        409: problemResponse('The change’s day has begun, or it is already withdrawn.', 'conflict'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
//# sourceMappingURL=tax-registration.js.map